import { resolveFocusedBrowseFolderPath } from '@/lib/resolve-create-folder-path'
import type { OpenTab } from '@/components/features/document-tabs'
import type { Workspace } from '@/providers/workspace-provider'
import { fetchWithAuth } from '@/lib/api/core'

/** Folder path for Google Drive imports under a workspace content root. */
export function driveImportFolderPath(contentRoot: string | null | undefined): string {
  const root = contentRoot?.trim().replace(/\/+$/, '')
  return root ? `${root}/google-drive-imports` : 'personal/google-drive-imports'
}

function normalizeStoragePath(path: string | null | undefined): string {
  return (path || '').replace(/^\/+/, '').replace(/\/+$/, '')
}

/** Resolve workspace id from a Maater storage folder path (contentRoot prefix match). */
export function organizationIdForFolderPath(
  folderPath: string,
  workspaces: Array<{ id: string; contentRoot?: string | null }>,
): string | undefined {
  const normalized = normalizeStoragePath(folderPath)
  if (!normalized) return undefined
  const match = workspaces.find((ws) => {
    const root = normalizeStoragePath(ws.contentRoot)
    return Boolean(root) && (normalized === root || normalized.startsWith(`${root}/`))
  })
  return match?.id
}

export type DriveImportWorkspace = Pick<Workspace, 'id' | 'name' | 'contentRoot'>

export type DriveImportDestination = {
  organizationId?: string
  folderPath: string
  fromBrowseContext: boolean
}

/**
 * Import destination: current Browse folder or open document parent when the
 * user is in a folder/viewer; otherwise `{contentRoot}/google-drive-imports`.
 */
export function resolveDriveImportDestination(opts: {
  explicitFolderPath?: string | null
  activeTab?: OpenTab
  selectedProjectId?: string | null
  fallbackOrganizationId?: string
  workspaces: Array<{ id: string; contentRoot?: string | null }>
}): DriveImportDestination {
  const explicit = normalizeStoragePath(opts.explicitFolderPath)
  const focused = resolveFocusedBrowseFolderPath({
    activeTab: opts.activeTab,
    selectedProjectId: opts.selectedProjectId,
  })
  const fallbackRoot = opts.workspaces.find((ws) => ws.id === opts.fallbackOrganizationId)?.contentRoot
  const folderPath = explicit || focused || driveImportFolderPath(fallbackRoot)
  const organizationId =
    organizationIdForFolderPath(folderPath, opts.workspaces) || opts.fallbackOrganizationId
  return {
    organizationId,
    folderPath,
    fromBrowseContext: Boolean(explicit || focused),
  }
}

export const GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT = 'google-drive-import-complete'

export const GOOGLE_DRIVE_OPEN_IMPORT_PICKER_EVENT = 'google-drive-open-import-picker'

/** Open the destination picker for the active Drive preview tab. */
export const DRIVE_PREVIEW_OPEN_IMPORT_EVENT = 'maater-drive-preview-open-import'

export type GoogleDriveImportCompleteDetail = {
  documentId?: string
  folderPath: string
  organizationId?: string
}

export type GoogleDriveOpenImportPickerDetail = {
  organizationId?: string
  initialUrl?: string
  folderPath?: string
}

export function openGoogleDriveImportPicker(
  detail: GoogleDriveOpenImportPickerDetail = {},
): void {
  window.dispatchEvent(new CustomEvent(GOOGLE_DRIVE_OPEN_IMPORT_PICKER_EVENT, { detail }))
}

export type DriveImportFile = {
  fileId: string
  name: string
  mimeType: string
}

export type DriveImportResult = {
  fileId: string
  documentId?: string
  title?: string
  status: 'done' | 'error'
  error?: string
}

type ImportProgressStatus = 'importing' | 'done' | 'error'

/** Import Drive files one at a time so the progress UI can update per row. */
export async function importDriveFilesSequentially(options: {
  organizationId: string
  files: DriveImportFile[]
  folderPath?: string
  onProgress?: (
    fileId: string,
    status: ImportProgressStatus,
    result?: DriveImportResult,
  ) => void
}): Promise<DriveImportResult[]> {
  const { organizationId, files, folderPath, onProgress } = options
  const results: DriveImportResult[] = []

  for (const file of files) {
    onProgress?.(file.fileId, 'importing')
    try {
      const res = await fetchWithAuth('/api/google-drive/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organizationId,
          folderPath,
          files: [file],
        }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        const failed: DriveImportResult = {
          fileId: file.fileId,
          status: 'error',
          error: body.error ?? `HTTP ${res.status}`,
        }
        results.push(failed)
        onProgress?.(file.fileId, 'error', failed)
        continue
      }

      const data = (await res.json()) as {
        results?: Array<{
          fileId?: string
          documentId?: string
          title?: string
          status?: string
          error?: string
        }>
      }
      const row = data.results?.[0]
      const success: DriveImportResult =
        row?.status === 'done'
          ? {
              fileId: file.fileId,
              documentId: row.documentId,
              title: row.title,
              status: 'done',
            }
          : {
              fileId: file.fileId,
              status: 'error',
              error: row?.error ?? 'Import failed',
            }
      results.push(success)
      onProgress?.(file.fileId, success.status, success)
    } catch (err) {
      const failed: DriveImportResult = {
        fileId: file.fileId,
        status: 'error',
        error: err instanceof Error ? err.message : 'Import failed',
      }
      results.push(failed)
      onProgress?.(file.fileId, 'error', failed)
    }
  }

  return results
}
